import React, { useEffect, useMemo, useState, useCallback } from "react";
import { collection, getDocs, query, orderBy } from "firebase/firestore";
import {
	AreaChart,
	Area,
	BarChart,
	Bar,
	PieChart,
	Pie,
	Cell,
	XAxis,
	YAxis,
	CartesianGrid,
	Legend,
	Tooltip,
	ResponsiveContainer,
} from "recharts";

import { db as defaultDb } from "../firebase";
import { supabase } from "../supabaseClient"; // adjust to your actual supabase client init path

import "./styles/insightsDashboard.css";

/* ---------------------------------------------------------
   CONFIG
--------------------------------------------------------- */

const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const PALETTE = ["#7A8B6F", "#C97B5A", "#B7A98F", "#5E6B54", "#DDBB99"]; // sage / terracotta family

// Matches the ids stored by the booking form's referral chips
const REFERRAL_LABELS = {
	linkedin: "LinkedIn",
	facebook: "Facebook",
	tiktok: "TikTok",
	youtube: "YouTube",
	google: "Google",
	instagram: "Instagram",
};
const REFERRAL_PALETTE = ["#0A66C2", "#C97B5A", "#5E6B54", "#B7A98F"]; // LinkedIn blue first so it reads at a glance

const AI_FUNCTION_NAME = "generate-ai-insights"; // supabase/functions/generate-ai-insights
const AI_CACHE_KEY = "jabuzwane_ai_insights_cache_v1";
const AI_CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24h — mirrors the server-side cache

const INSIGHT_TYPE_ORDER = { warning: 0, opportunity: 1, positive: 2 };

/* ---------------------------------------------------------
   INSIGHT ENGINE
   Pure functions that turn raw bookings into decision-grade
   insights. Kept outside the component so they're easy to
   unit test independently of Firestore/React. This is also
   the payload the AI layer reasons over — it does not see
   raw booking records, only these aggregates.
--------------------------------------------------------- */

function toDateSafe(b) {
	// booking.date is "YYYY-MM-DD", booking.time is "HH:mm"
	return new Date(`${b.date}T${b.time || "00:00"}:00`);
}

function startOfWeek(d) {
	const date = new Date(d);
	const day = date.getDay();
	date.setDate(date.getDate() - day);
	date.setHours(0, 0, 0, 0);
	return date;
}

function groupCount(items, keyFn) {
	const map = {};
	items.forEach((item) => {
		const key = keyFn(item);
		map[key] = (map[key] || 0) + 1;
	});
	return map;
}

function pct(part, whole) {
	if (!whole) return 0;
	return Math.round((part / whole) * 1000) / 10;
}

/**
 * Builds the full insights payload from a flat array of booking docs.
 */
function computeInsights(bookings) {
	if (!bookings.length) {
		return { insights: [], charts: {}, stats: {} };
	}

	const now = new Date();
	const thisWeekStart = startOfWeek(now);
	const lastWeekStart = new Date(thisWeekStart);
	lastWeekStart.setDate(lastWeekStart.getDate() - 7);
	const twoWeeksAgoStart = new Date(thisWeekStart);
	twoWeeksAgoStart.setDate(twoWeeksAgoStart.getDate() - 14);

	const withDates = bookings.map((b) => ({ ...b, _dt: toDateSafe(b) }));

	const thisWeek = withDates.filter((b) => b._dt >= thisWeekStart);
	const lastWeek = withDates.filter(
		(b) => b._dt >= lastWeekStart && b._dt < thisWeekStart
	);
	const prevWeek = withDates.filter(
		(b) => b._dt >= twoWeeksAgoStart && b._dt < lastWeekStart
	);

	const confirmed = withDates.filter((b) => b.status === "confirmed");
	const cancelled = withDates.filter((b) => b.status === "cancelled");
	const pending = withDates.filter((b) => b.status === "pending");

	const thisWeekCancelled = thisWeek.filter((b) => b.status === "cancelled");
	const lastWeekCancelled = lastWeek.filter((b) => b.status === "cancelled");

	/* --- day-of-week distribution (confirmed only, so cancellations don't skew demand) --- */
	const byDay = groupCount(confirmed, (b) => b._dt.getDay());
	const dayTotals = DAY_NAMES.map((name, idx) => ({
		day: name,
		count: byDay[idx] || 0,
	}));
	const avgPerDay =
		dayTotals.reduce((sum, d) => sum + d.count, 0) / dayTotals.length;
	const busiest = [...dayTotals].sort((a, b) => b.count - a.count)[0];
	const quietest = [...dayTotals].sort((a, b) => a.count - b.count)[0];

	/* --- service popularity --- */
	const byService = groupCount(confirmed, (b) => b.serviceTitle || "Unknown");
	const serviceTotals = Object.entries(byService)
		.map(([name, count]) => ({ name, count }))
		.sort((a, b) => b.count - a.count);
	const topService = serviceTotals[0];

	/* --- mode split --- */
	const byMode = groupCount(confirmed, (b) => b.mode || "unspecified");
	const modeTotals = Object.entries(byMode).map(([name, count]) => ({
		name,
		count,
	}));

	/* --- time-of-day popularity --- */
	const byTime = groupCount(confirmed, (b) => b.time || "unknown");
	const timeTotals = Object.entries(byTime).sort((a, b) => b[1] - a[1]);
	const peakTime = timeTotals[0];

	/* --- referral source popularity ("how did you hear about us?") ---
	   Only counts bookings where the client actually chose to answer —
	   the field is optional, so skipped ones (null) are excluded here
	   rather than diluting the channel breakdown. */
	const referralProvided = confirmed.filter((b) => b.referralSource);
	const byReferral = groupCount(referralProvided, (b) => b.referralSource);
	const referralTotals = Object.entries(byReferral)
		.map(([id, count]) => ({
			id,
			name: REFERRAL_LABELS[id] || id,
			count,
		}))
		.sort((a, b) => b.count - a.count);
	const topReferral = referralTotals[0];
	const referralResponseRate = pct(referralProvided.length, confirmed.length);

	/* --- 8-week trend for the area chart --- */
	const weeks = [];
	for (let i = 7; i >= 0; i--) {
		const wkStart = new Date(thisWeekStart);
		wkStart.setDate(wkStart.getDate() - i * 7);
		const wkEnd = new Date(wkStart);
		wkEnd.setDate(wkEnd.getDate() + 7);
		const count = withDates.filter(
			(b) => b._dt >= wkStart && b._dt < wkEnd && b.status !== "cancelled"
		).length;
		weeks.push({
			label: `${wkStart.getMonth() + 1}/${wkStart.getDate()}`,
			bookings: count,
		});
	}

	/* -----------------------------------------------------
	   Generate the plain-language, decision-oriented insights
	----------------------------------------------------- */
	const insights = [];

	// 1. Week-over-week momentum
	const wowChange = pct(thisWeek.length - lastWeek.length, lastWeek.length || 1);
	if (lastWeek.length > 0) {
		if (wowChange >= 15) {
			insights.push({
				type: "positive",
				title: "Bookings are trending up",
				message: `Confirmed bookings are up ${wowChange}% vs last week (${thisWeek.length} vs ${lastWeek.length}). Consider making sure you have capacity to keep up.`,
			});
		} else if (wowChange <= -15) {
			insights.push({
				type: "warning",
				title: "Bookings dropped this week",
				message: `Bookings are down ${Math.abs(
					wowChange
				)}% vs last week (${thisWeek.length} vs ${lastWeek.length}). Worth checking if a promo, reminder email, or availability gap explains it.`,
			});
		}
	}

	// 2. Two-week trend direction (smooths out noise)
	if (prevWeek.length > 0 && lastWeek.length > 0) {
		const trendChange = pct(lastWeek.length - prevWeek.length, prevWeek.length);
		if (Math.sign(trendChange) === Math.sign(wowChange) && Math.abs(trendChange) >= 10) {
			insights.push({
				type: trendChange > 0 ? "positive" : "warning",
				title: trendChange > 0 ? "Two-week growth streak" : "Two-week decline",
				message: `Bookings have moved ${trendChange > 0 ? "up" : "down"} for two weeks running. This looks like a real trend, not a one-off.`,
			});
		}
	}

	// 3. Quiet day opportunity
	if (quietest.count < avgPerDay * 0.6 && avgPerDay > 1) {
		insights.push({
			type: "opportunity",
			title: `${quietest.day}s are underbooked`,
			message: `${quietest.day} averages ${quietest.count} bookings vs a ${avgPerDay.toFixed(
				1
			)} daily average. A midweek discount or targeted reminder could fill that gap.`,
		});
	}

	// 4. Busiest day capacity flag
	if (busiest.count > avgPerDay * 1.5 && avgPerDay > 1) {
		insights.push({
			type: "warning",
			title: `${busiest.day} is your bottleneck`,
			message: `${busiest.day} carries ${pct(
				busiest.count,
				confirmed.length
			)}% of all bookings. If it's consistently maxed out, clients may be getting turned away — consider opening more slots.`,
		});
	}

	// 5. Service concentration
	if (topService && serviceTotals.length > 1) {
		const share = pct(topService.count, confirmed.length);
		if (share >= 40) {
			insights.push({
				type: "opportunity",
				title: `"${topService.name}" dominates demand`,
				message: `It accounts for ${share}% of bookings. Consider promoting your other services more, or raising pricing/capacity for this one if margins allow.`,
			});
		}
	}

	// 6. Mode preference
	const virtualCount = byMode["virtual"] || 0;
	const inPersonCount = confirmed.length - virtualCount;
	if (confirmed.length > 0) {
		const virtualShare = pct(virtualCount, confirmed.length);
		if (virtualShare >= 65) {
			insights.push({
				type: "positive",
				title: "Clients strongly prefer virtual",
				message: `${virtualShare}% of bookings are virtual. You could likely reduce in-person overhead or reallocate that time to more virtual slots.`,
			});
		} else if (virtualShare <= 35 && confirmed.length >= 5) {
			insights.push({
				type: "positive",
				title: "Clients strongly prefer in-person",
				message: `Only ${virtualShare}% of bookings are virtual. In-person demand is dominant — make sure physical scheduling capacity matches this.`,
			});
		}
	}

	// 7. Cancellation rate
	const cancelRate = pct(cancelled.length, withDates.length);
	const thisWeekCancelRate = pct(thisWeekCancelled.length, thisWeek.length);
	const lastWeekCancelRate = pct(lastWeekCancelled.length, lastWeek.length);
	if (thisWeek.length >= 3 && thisWeekCancelRate - lastWeekCancelRate >= 10) {
		insights.push({
			type: "warning",
			title: "Cancellations are climbing",
			message: `This week's cancellation rate is ${thisWeekCancelRate}%, up from ${lastWeekCancelRate}% last week. Consider a confirmation/reminder message 24h before sessions.`,
		});
	} else if (cancelRate >= 20) {
		insights.push({
			type: "warning",
			title: "Overall cancellation rate is high",
			message: `${cancelRate}% of all bookings end up cancelled. A deposit or stronger reminder flow could recover a meaningful chunk of this.`,
		});
	}

	// 8. Peak time slot
	if (peakTime) {
		const [time, count] = peakTime;
		const share = pct(count, confirmed.length);
		if (share >= 25) {
			insights.push({
				type: "opportunity",
				title: `${time} is your most requested slot`,
				message: `${share}% of bookings land on ${time}. Consider opening an adjacent slot (e.g. one before/after) to capture overflow demand.`,
			});
		}
	}

	// 9. Pending backlog
	if (pending.length >= 3) {
		insights.push({
			type: "warning",
			title: `${pending.length} bookings awaiting confirmation`,
			message: `These haven't been marked confirmed yet. Following up quickly reduces no-show and drop-off risk.`,
		});
	}

	// 10. Top referral / social channel
	if (topReferral && referralProvided.length >= 3) {
		const share = pct(topReferral.count, referralProvided.length);
		if (share >= 30) {
			insights.push({
				type: "opportunity",
				title: `${topReferral.name} drives the most bookings`,
				message: `Among clients who shared how they found you, ${share}% came via ${topReferral.name} (${topReferral.count} of ${referralProvided.length}). Worth doubling down on ${topReferral.name} content or ads.`,
			});
		}
	}

	// 11. Low response rate on "how did you hear about us"
	if (confirmed.length >= 8 && referralResponseRate < 25) {
		insights.push({
			type: "warning",
			title: "Most clients skip the referral question",
			message: `Only ${referralResponseRate}% of bookings say how they found you. Nudging clients to answer (even briefly) would sharpen where to focus marketing spend.`,
		});
	}

	// Priority sort: warnings first (need attention), then opportunities, then good news
	insights.sort((a, b) => INSIGHT_TYPE_ORDER[a.type] - INSIGHT_TYPE_ORDER[b.type]);

	return {
		insights,
		charts: {
			weeklyTrend: weeks,
			dayTotals,
			serviceTotals: serviceTotals.slice(0, 6),
			modeTotals,
			referralTotals,
		},
		stats: {
			total: withDates.length,
			confirmed: confirmed.length,
			cancelled: cancelled.length,
			pending: pending.length,
			cancelRate,
			thisWeek: thisWeek.length,
			wowChange,
			virtualShare: pct(virtualCount, confirmed.length || 1),
			topService: topService?.name,
			busiestDay: busiest.day,
			quietestDay: quietest.day,
			peakTime: peakTime ? peakTime[0] : null,
			topReferralSource: topReferral?.name || null,
			topReferralShare: topReferral
				? pct(topReferral.count, referralProvided.length)
				: null,
			referralResponseRate,
		},
	};
}

/* ---------------------------------------------------------
   AI LAYER
   Calls the Groq-backed serverless function with the
   already-computed stats. Cached client-side (localStorage,
   24h) as a cheap first line of defense on top of the
   server-side Firestore cache.
--------------------------------------------------------- */

function readAiCache(statsKey) {
	try {
		const raw = localStorage.getItem(AI_CACHE_KEY);
		if (!raw) return null;
		const parsed = JSON.parse(raw);
		if (
			parsed &&
			parsed.statsKey === statsKey &&
			Date.now() - parsed.generatedAt < AI_CACHE_TTL_MS
		) {
			return parsed;
		}
	} catch {
		// corrupt cache, ignore
	}
	return null;
}

function writeAiCache(result, statsKey) {
	try {
		localStorage.setItem(
			AI_CACHE_KEY,
			JSON.stringify({ ...result, statsKey })
		);
	} catch {
		// storage full/unavailable — non-fatal, AI panel just won't cache client-side
	}
}

function useAiInsights(stats, charts) {
	const [state, setState] = useState({
		loading: false,
		error: null,
		data: null,
		dataKey: null,
	});
	const statsKey = useMemo(() => JSON.stringify({ stats, charts }), [stats, charts]);

	const fetchInsights = useCallback(
		async (forceRefresh = false) => {
			if (!stats || !stats.total) return;

			if (!forceRefresh) {
				const cached = readAiCache(statsKey);
				if (cached) {
					setState({ loading: false, error: null, data: cached, dataKey: statsKey });
					return;
				}
			}

			setState((current) => ({
				...current,
				loading: true,
				error: null,
				data: current.dataKey === statsKey ? current.data : null,
			}));
			try {
				// supabase.functions.invoke handles the edge function URL,
				// apikey/Authorization headers, and CORS for you.
				const { data, error: invokeError } = await supabase.functions.invoke(
					AI_FUNCTION_NAME,
					{ body: { stats, charts, forceRefresh } }
				);
				if (invokeError) throw invokeError;
				if (data?.error) throw new Error(data.error);
				writeAiCache(data, statsKey);
				setState({ loading: false, error: null, data, dataKey: statsKey });
			} catch (err) {
				setState((current) => ({
					loading: false,
					error: err.message,
					data: current.dataKey === statsKey ? current.data : null,
					dataKey: current.dataKey,
				}));
			}
		},
		[stats, charts, statsKey]
	);

	useEffect(() => {
		fetchInsights(false);
	}, [fetchInsights]);

	return { ...state, refresh: () => fetchInsights(true) };
}

/* ---------------------------------------------------------
   ICONS (inline SVG, no extra dependency)
--------------------------------------------------------- */

const Icon = {
	trend: (p) => (
		<svg viewBox="0 0 24 24" fill="none" {...p}>
			<path d="M3 17l6-6 4 4 8-8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
			<path d="M15 7h6v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
		</svg>
	),
	calendar: (p) => (
		<svg viewBox="0 0 24 24" fill="none" {...p}>
			<rect x="3" y="5" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="2" />
			<path d="M3 10h18M8 3v4M16 3v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
		</svg>
	),
	clock: (p) => (
		<svg viewBox="0 0 24 24" fill="none" {...p}>
			<circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
			<path d="M12 7v5l3 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
		</svg>
	),
	star: (p) => (
		<svg viewBox="0 0 24 24" fill="none" {...p}>
			<path d="M12 3l2.6 5.9 6.4.6-4.8 4.3 1.4 6.2L12 16.9 6.4 20l1.4-6.2L3 9.5l6.4-.6L12 3z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
		</svg>
	),
	globe: (p) => (
		<svg viewBox="0 0 24 24" fill="none" {...p}>
			<circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
			<path d="M3 12h18M12 3c2.5 2.6 3.8 5.7 3.8 9s-1.3 6.4-3.8 9c-2.5-2.6-3.8-5.7-3.8-9s1.3-6.4 3.8-9z" stroke="currentColor" strokeWidth="2" />
		</svg>
	),
	megaphone: (p) => (
		<svg viewBox="0 0 24 24" fill="none" {...p}>
			<path d="M3 10v4a1 1 0 001 1h2l7 4V5L6 9H4a1 1 0 00-1 1z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
			<path d="M17 8a5 5 0 010 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
		</svg>
	),
	sparkle: (p) => (
		<svg viewBox="0 0 24 24" fill="none" {...p}>
			<path d="M12 2l1.8 5.6L19 9l-5.2 1.4L12 16l-1.8-5.6L5 9l5.2-1.4L12 2z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
			<path d="M19 15l.8 2.3L22 18l-2.2.7L19 21l-.8-2.3L16 18l2.2-.7L19 15z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
		</svg>
	),
	refresh: (p) => (
		<svg viewBox="0 0 24 24" fill="none" {...p}>
			<path d="M3 12a9 9 0 0115.4-6.4M21 12a9 9 0 01-15.4 6.4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
			<path d="M18 3v5h-5M6 21v-5h5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
		</svg>
	),
	alert: (p) => (
		<svg viewBox="0 0 24 24" fill="none" {...p}>
			<path d="M12 9v4M12 17h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
			<path d="M10.3 3.9L2.5 18a1 1 0 00.9 1.5h17.2a1 1 0 00.9-1.5L13.7 3.9a1 1 0 00-1.7 0z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
		</svg>
	),
};

/* ---------------------------------------------------------
   COMPONENT
--------------------------------------------------------- */

const InsightsDashboard = ({ db = defaultDb }) => {
	const [bookings, setBookings] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [lastUpdated, setLastUpdated] = useState(null);

	const fetchBookings = useCallback(async () => {
		try {
			setLoading(true);
			setError(null);
			const q = query(collection(db, "bookings"), orderBy("bookedAt", "desc"));
			const snap = await getDocs(q);
			setBookings(snap.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
			setLastUpdated(new Date());
		} catch (err) {
			console.error("Failed to load bookings:", err);
			setError("Couldn't load booking data. Check your connection and try again.");
		} finally {
			setLoading(false);
		}
	}, [db]);

	useEffect(() => {
		fetchBookings();
	}, [fetchBookings]);

	const { insights, charts, stats } = useMemo(
		() => computeInsights(bookings),
		[bookings]
	);

	const ai = useAiInsights(stats, charts);

	if (loading && !lastUpdated) {
		return (
			<div className="insights-dashboard" aria-busy="true" aria-label="Loading booking insights">
				<div className="skeleton skeleton-header" />
				<div className="stat-grid">
					{Array.from({ length: 4 }).map((_, i) => (
						<div key={i} className="skeleton skeleton-stat" />
					))}
				</div>
				<div className="skeleton skeleton-block" />
				<div className="skeleton skeleton-block" />
			</div>
		);
	}

	if (error && !bookings.length) {
		return (
			<div className="insights-state insights-state--error" role="alert">
				<Icon.alert className="insights-state__icon" />
				<h2>Insights couldn’t load</h2>
				<p>{error}</p>
				<button type="button" className="insights-retry-btn" onClick={fetchBookings} disabled={loading}>
					<Icon.refresh className={loading ? "spin" : ""} />
					{loading ? "Retrying…" : "Try again"}
				</button>
			</div>
		);
	}

	return (
		<div className="insights-dashboard">
			<header className="insights-header">
				<div className="insights-header__copy">
					<span className="insights-eyebrow">Performance overview</span>
					<h2>AI Based Insights</h2>
					<p className="insights-subtitle">Understand booking patterns and spot practical opportunities for your business.</p>
				</div>
				<div className="insights-header__actions">
					{lastUpdated && (
						<span className="insights-updated">
							<span className="insights-updated__dot" />
							Updated {lastUpdated.toLocaleTimeString("en-ZA", { hour: "2-digit", minute: "2-digit" })}
						</span>
					)}
					<button type="button" className="insights-retry-btn insights-retry-btn--subtle" onClick={fetchBookings} disabled={loading}>
						<Icon.refresh className={loading ? "spin" : ""} />
						{loading ? "Updating…" : "Refresh data"}
					</button>
				</div>
			</header>

			{error && (
				<div className="insights-inline-error" role="alert">
					<Icon.alert />
					<span>{error} Showing the last successfully loaded data.</span>
				</div>
			)}

			{!bookings.length ? (
				<div className="insights-empty">
					<span className="insights-empty__icon"><Icon.sparkle /></span>
					<h3>Your insights will appear here</h3>
					<p>Once clients make bookings, this page will highlight trends, popular services, and areas to focus on.</p>
				</div>
			) : (
				<>
					<section className="insights-kpis" aria-label="Booking summary">
						<StatCard icon={Icon.calendar} label="All bookings" value={stats.total} />
						<StatCard icon={Icon.trend} label="Confirmed" value={stats.confirmed} />
						<StatCard icon={Icon.clock} label="Awaiting confirmation" value={stats.pending} />
						<StatCard icon={Icon.alert} label="Cancellation rate" value={`${stats.cancelRate}%`} sublabel={`${stats.cancelled} bookings`} />
					</section>

			{/* ---- AI narrative panel ---- */}
			<AIInsightsPanel ai={ai} />

			{/* ---- Booking patterns ---- */}
			<section className="insight-section">
				<div className="section-heading-wrap">
					<div>
						<h3 className="section-heading">Booking patterns</h3>
						<p className="section-description">Signals from your confirmed bookings and recent activity.</p>
					</div>
				</div>
				<div className="stat-grid">
				<StatCard icon={Icon.trend} label="This week" value={stats.thisWeek} delta={stats.wowChange} />
				<StatCard icon={Icon.calendar} label="Busiest day" value={stats.busiestDay} />
				<StatCard icon={Icon.calendar} label="Quietest day" value={stats.quietestDay} />
				<StatCard icon={Icon.clock} label="Peak time slot" value={stats.peakTime || "—"} />
				<StatCard icon={Icon.star} label="Top service" value={stats.topService || "—"} />
				<StatCard icon={Icon.globe} label="Virtual share" value={`${stats.virtualShare}%`} />
				<StatCard
					icon={Icon.megaphone}
					label="Top social channel"
					value={stats.topReferralSource || "No data yet"}
					sublabel={
						stats.topReferralSource
							? `${stats.topReferralShare}% of responses`
							: null
					}
				/>
			</div>
			</section>

			{/* ---- Actionable insight cards (rule-based, instant, free) ---- */}
			<section className="insight-section">
				<div className="section-heading-wrap">
					<div>
						<h3 className="section-heading">What the numbers show</h3>
						<p className="section-description">Prioritised alerts, trends, and opportunities based on your booking data.</p>
					</div>
					<span className="insights-count">{insights.length} {insights.length === 1 ? "insight" : "insights"}</span>
				</div>
				<div className="insight-cards">
					{insights.length === 0 ? (
						<div className="insight-card insight-card--positive">
							<span className="insight-tag">Steady</span>
							<h4>Nothing unusual right now</h4>
							<p>Booking patterns look stable — no red flags or standout opportunities this period.</p>
						</div>
					) : (
						insights.map((insight, i) => (
							<div key={i} className={`insight-card insight-card--${insight.type}`}>
								<span className="insight-tag">
									{insight.type === "positive"
										? "Trend"
										: insight.type === "warning"
										? "Needs attention"
										: "Opportunity"}
								</span>
								<h4>{insight.title}</h4>
								<p>{insight.message}</p>
							</div>
						))
					)}
				</div>
			</section>

			{/* ---- Supporting charts ---- */}
			<section className="insight-section">
				<div className="section-heading-wrap">
					<div>
						<h3 className="section-heading">Explore your performance</h3>
						<p className="section-description">Use these charts to see when clients book and which services or channels stand out.</p>
					</div>
				</div>
				<div className="chart-grid">
				<div className="chart-card">
					<h4>Weekly booking trend</h4>
					<ResponsiveContainer width="100%" height={220}>
						<AreaChart data={charts.weeklyTrend}>
							<CartesianGrid strokeDasharray="3 3" stroke="#e8e3d9" />
							<XAxis dataKey="label" fontSize={12} />
							<YAxis fontSize={12} allowDecimals={false} />
							<Tooltip />
							<Area
								type="monotone"
								dataKey="bookings"
								stroke={PALETTE[0]}
								fill={PALETTE[0]}
								fillOpacity={0.25}
							/>
						</AreaChart>
					</ResponsiveContainer>
				</div>

				<div className="chart-card">
					<h4>Bookings by day of week</h4>
					<ResponsiveContainer width="100%" height={220}>
						<BarChart data={charts.dayTotals}>
							<CartesianGrid strokeDasharray="3 3" stroke="#e8e3d9" />
							<XAxis dataKey="day" fontSize={12} />
							<YAxis fontSize={12} allowDecimals={false} />
							<Tooltip />
							<Bar dataKey="count" fill={PALETTE[1]} radius={[4, 4, 0, 0]} />
						</BarChart>
					</ResponsiveContainer>
				</div>

				<div className="chart-card">
					<h4>Service popularity</h4>
					<ResponsiveContainer width="100%" height={220}>
						<BarChart data={charts.serviceTotals} layout="vertical">
							<CartesianGrid strokeDasharray="3 3" stroke="#e8e3d9" />
							<XAxis type="number" fontSize={12} allowDecimals={false} />
							<YAxis type="category" dataKey="name" width={140} fontSize={11} />
							<Tooltip />
							<Bar dataKey="count" fill={PALETTE[2]} radius={[0, 4, 4, 0]} />
						</BarChart>
					</ResponsiveContainer>
				</div>

				<div className="chart-card">
					<h4>Virtual vs in-person</h4>
					<ResponsiveContainer width="100%" height={220}>
						<PieChart>
							<Pie
								data={charts.modeTotals}
								dataKey="count"
								nameKey="name"
								innerRadius={55}
								outerRadius={80}
								paddingAngle={3}
							>
								{charts.modeTotals.map((entry, i) => (
									<Cell key={entry.name} fill={PALETTE[i % PALETTE.length]} />
								))}
							</Pie>
							<Legend verticalAlign="bottom" height={28} iconType="circle" wrapperStyle={{ fontSize: "11px" }} />
							<Tooltip />
						</PieChart>
					</ResponsiveContainer>
				</div>

				<div className="chart-card">
					<h4>How clients found you</h4>
					{charts.referralTotals && charts.referralTotals.length > 0 ? (
						<>
							<ResponsiveContainer width="100%" height={220}>
								<PieChart>
									<Pie
										data={charts.referralTotals}
										dataKey="count"
										nameKey="name"
										innerRadius={55}
										outerRadius={80}
										paddingAngle={3}
									>
										{charts.referralTotals.map((entry, i) => (
											<Cell
												key={entry.id}
												fill={REFERRAL_PALETTE[i % REFERRAL_PALETTE.length]}
											/>
										))}
									</Pie>
									<Legend verticalAlign="bottom" height={28} iconType="circle" wrapperStyle={{ fontSize: "11px" }} />
									<Tooltip />
								</PieChart>
							</ResponsiveContainer>
							<p className="chart-footnote">
								Based on {stats.referralResponseRate}% of confirmed bookings
								that answered this optional question.
							</p>
						</>
					) : (
						<p className="chart-empty-state">
							No one has answered "how did you hear about us?" yet.
						</p>
					)}
				</div>
				</div>
			</section>
				</>
			)}
		</div>
	);
};

/* ---------------------------------------------------------
   AI INSIGHTS PANEL
--------------------------------------------------------- */

const AIInsightsPanel = ({ ai }) => {
	const { loading, error, data, refresh } = ai;

	return (
		<section className="ai-panel">
			<div className="ai-panel-header">
				<div className="ai-panel-title">
					<Icon.sparkle className="ai-sparkle-icon" />
					<h3>AI Analysis</h3>
					<span className="ai-badge">Groq · Llama 3.3</span>
				</div>
				<button
					className="ai-refresh-btn"
					onClick={refresh}
					disabled={loading}
					title="Regenerate AI analysis"
				>
					<Icon.refresh className={loading ? "spin" : ""} />
					{loading ? "Thinking…" : "Refresh"}
				</button>
			</div>

			{error && (
				<div className="ai-panel-error">
					<Icon.alert />
					<span>Couldn't generate AI insights right now. The numbers below are still accurate.</span>
				</div>
			)}

			{loading && !data && (
				<div className="ai-panel-body">
					<div className="skeleton skeleton-line skeleton-line--wide" />
					<div className="skeleton skeleton-line" />
					<div className="skeleton skeleton-line skeleton-line--short" />
				</div>
			)}

			{data && (
				<div className="ai-panel-body">
					{data.headline && <p className="ai-headline">{data.headline}</p>}
					{data.summary && <p className="ai-summary">{data.summary}</p>}
					{data.recommendations && data.recommendations.length > 0 && (
						<ul className="ai-recommendations">
							{data.recommendations.map((rec, i) => (
								<li key={i} className={`ai-rec ai-rec--${rec.priority || "medium"}`}>
									<span className="ai-rec-priority">{rec.priority || "medium"}</span>
									<div>
										<strong>{rec.title}</strong>
										<p>{rec.detail}</p>
									</div>
								</li>
							))}
						</ul>
					)}
				</div>
			)}
		</section>
	);
};

const StatCard = ({ icon: IconComp, label, value, delta, sublabel }) => (
	<div className="stat-card">
		{IconComp && <IconComp className="stat-icon" />}
		<span className="stat-label">{label}</span>
		<span className="stat-value">{value}</span>
		{typeof delta === "number" && delta !== 0 && (
			<span className={`stat-delta ${delta > 0 ? "up" : "down"}`}>
				{delta > 0 ? "▲" : "▼"} {Math.abs(delta)}%
			</span>
		)}
		{sublabel && <span className="stat-sublabel">{sublabel}</span>}
	</div>
);

export default InsightsDashboard;